import { useDamp, useOrbit } from '#imports';
import { whenever } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import { useRafBool, useWindowPointer } from '@resn/gozer-vue';

export const useManualPackRip = ({
    axis = 'x',
    maxDistance = ref(200),
    onRipping = () => {},
    onFullyRipping = () => {},
    onUpdate = () => {},
    onComplete = () => {},
} = {}) => {
    const isEnabled = ref(false);
    const ripProgress = ref(0);
    const reachedThreshold = ref(false);
    const startPos = ref(0);
    const isSliderDragging = ref(false);
    const orbit = useOrbit();
    const data = { progress: 0 };
    // const maxDistance = ref(200);

    const SLIDER_COMPLETION_THRESHOLD = 0.98;
    let lastSliderProgress = 0;

    const isRipping = computed(() => {
        return isEnabled.value && ((isDown.value && !orbit.value) || isSliderDragging.value);
    });

    const isFullyRipping = ref(false);

    const progressDamp = useDamp(data, { lambda: 8 }, ['progress']);

    const { isDown } = useWindowPointer(({ x, y, fromDown }) => {
        if (!isEnabled.value || orbit.value) return;

        const pos = axis === 'x' ? x : y;

        if (fromDown) {
            startPos.value = pos;
        }

        if (isDown.value) {
            const delta = Math.max(0, pos - startPos.value);
            const progress = delta / maxDistance.value;

            progressDamp.set({ progress });
        }
    });

    const setPointerDown = (val) => {
        if (!isEnabled.value || orbit.value) return;

        const { config } = progressDamp.state;

        if (val) {
            config.lambda = 20;
        } else {
            progressDamp.set({ progress: 0 });
            config.lambda = 8;
        }
    };

    const setIsRipping = (val) => {
        onRipping(val, reachedThreshold.value);
    };

    const setIsFullyRipping = (val) => {
        onFullyRipping(val);
    };

    watch(isDown, setPointerDown);
    watch(isRipping, setIsRipping);
    watch(isFullyRipping, setIsFullyRipping);

    const enable = () => {
        isEnabled.value = true;
    };

    const disable = () => {
        isEnabled.value = false;
    };

    const setProgress = (progress) => {
        lastSliderProgress = progress;

        const { config } = progressDamp.state;
        config.lambda = 10;
        progressDamp.set({ progress });
    };

    const setSliderDragging = (val) => {
        if (!isEnabled.value) return;

        const { config } = progressDamp.state;

        if (val) {
            isSliderDragging.value = true;
            config.lambda = 10;
        } else {
            isSliderDragging.value = false;

            if (lastSliderProgress >= SLIDER_COMPLETION_THRESHOLD) {
                reachedThreshold.value = true;
                onComplete();
                disable();
            } else {
                progressDamp.set({ progress: 0 });
                config.lambda = 4;
            }

            lastSliderProgress = 0;
        }
    };

    useRafBool(isEnabled, () => {
        ripProgress.value = data.progress;
        isFullyRipping.value = isRipping.value && data.progress >= 0.1;

        onUpdate(data.progress);

        if (data.progress >= 1) {
            reachedThreshold.value = true;

            onComplete();
            disable();
        }
    });

    return {
        isEnabled,
        isRipping,
        isFullyRipping,
        ripProgress,
        reachedThreshold,
        enable,
        disable,
        setProgress,
        setSliderDragging,
        maxDistance,
    };
};
